/**
 * constants/economicCalendar.ts
 * Economic calendar constants for Finnhub API integration
 *
 * Provides runtime constants for the Economic Calendar domain including:
 * - Country-to-currency mapping for all Finnhub-supported countries
 * - Country-to-name mapping for UI display
 *
 * COUNTRY CODE FORMAT:
 * - Finnhub uses 2-letter country codes (mostly ISO 3166-1 alpha-2)
 * - Exception: "EU" represents Eurozone aggregate data (not an ISO country code)
 *
 * CURRENCY MAPPING POLICY:
 * - Eurozone members map to EUR
 * - Dollarized economies (EC, SV) map to USD
 * - Multi-currency countries map to their primary legal tender
 * - Reference: Wikipedia "List of circulating currencies"
 *
 * ARCHITECTURAL CONSTRAINTS:
 * - Zero cross-domain imports (no imports from `utils/` or other domain constants)
 * - No production side effects (dev-only validation guards are stripped from production builds)
 * - All exports use `as const satisfies` for compile-time type safety
 */

/**
 * Country code to currency code mapping for Finnhub economic calendar events
 *
 * Maps Finnhub's 86 country codes to their corresponding ISO 4217 currency codes.
 * Used to determine which currency is affected by economic events from each country.
 *
 * Structure:
 * - 19 Eurozone entries (18 member states + EU aggregate) → EUR
 * - 2 dollarized economies (EC, SV) → USD
 * - 1 additional EUR user (ME - Montenegro) → EUR
 * - 64 countries with their own currencies
 *
 * Sorted alphabetically by country code.
 */
export const COUNTRY_TO_CURRENCY = {
  'AE': 'AED', // United Arab Emirates → UAE Dirham
  'AL': 'ALL', // Albania → Albanian Lek
  'AO': 'AOA', // Angola → Angolan Kwanza
  'AR': 'ARS', // Argentina → Argentine Peso
  'AT': 'EUR', // Austria → Euro (Eurozone)
  'AU': 'AUD', // Australia → Australian Dollar
  'AZ': 'AZN', // Azerbaijan → Azerbaijani Manat
  'BD': 'BDT', // Bangladesh → Bangladeshi Taka
  'BE': 'EUR', // Belgium → Euro (Eurozone)
  'BG': 'BGN', // Bulgaria → Bulgarian Lev
  'BN': 'BND', // Brunei → Brunei Dollar
  'BR': 'BRL', // Brazil → Brazilian Real
  'BW': 'BWP', // Botswana → Botswana Pula
  'BY': 'BYN', // Belarus → Belarusian Ruble
  'CA': 'CAD', // Canada → Canadian Dollar
  'CD': 'CDF', // DR Congo → Congolese Franc
  'CH': 'CHF', // Switzerland → Swiss Franc
  'CN': 'CNY', // China → Chinese Yuan
  'CO': 'COP', // Colombia → Colombian Peso
  'CV': 'CVE', // Cape Verde → Cape Verdean Escudo
  'CZ': 'CZK', // Czechia → Czech Koruna
  'DE': 'EUR', // Germany → Euro (Eurozone)
  'DO': 'DOP', // Dominican Republic → Dominican Peso
  'EC': 'USD', // Ecuador → US Dollar (dollarized)
  'EE': 'EUR', // Estonia → Euro (Eurozone)
  'ER': 'ERN', // Eritrea → Eritrean Nakfa
  'ES': 'EUR', // Spain → Euro (Eurozone)
  'ET': 'ETB', // Ethiopia → Ethiopian Birr
  'EU': 'EUR', // European Union → Euro (aggregate data)
  'FI': 'EUR', // Finland → Euro (Eurozone)
  'FR': 'EUR', // France → Euro (Eurozone)
  'GB': 'GBP', // United Kingdom → British Pound
  'GE': 'GEL', // Georgia → Georgian Lari
  'GR': 'EUR', // Greece → Euro (Eurozone)
  'HK': 'HKD', // Hong Kong → Hong Kong Dollar
  'HR': 'EUR', // Croatia → Euro (Eurozone, joined 2023)
  'HU': 'HUF', // Hungary → Hungarian Forint
  'ID': 'IDR', // Indonesia → Indonesian Rupiah
  'IE': 'EUR', // Ireland → Euro (Eurozone)
  'IL': 'ILS', // Israel → Israeli New Shekel
  'IN': 'INR', // India → Indian Rupee
  'IR': 'IRR', // Iran → Iranian Rial
  'IT': 'EUR', // Italy → Euro (Eurozone)
  'JO': 'JOD', // Jordan → Jordanian Dinar
  'JP': 'JPY', // Japan → Japanese Yen
  'KG': 'KGS', // Kyrgyzstan → Kyrgyzstani Som
  'KM': 'KMF', // Comoros → Comorian Franc
  'KR': 'KRW', // South Korea → South Korean Won
  'KW': 'KWD', // Kuwait → Kuwaiti Dinar
  'KZ': 'KZT', // Kazakhstan → Kazakhstani Tenge
  'LK': 'LKR', // Sri Lanka → Sri Lankan Rupee
  'LT': 'EUR', // Lithuania → Euro (Eurozone)
  'LU': 'EUR', // Luxembourg → Euro (Eurozone)
  'LV': 'EUR', // Latvia → Euro (Eurozone)
  'MD': 'MDL', // Moldova → Moldovan Leu
  'ME': 'EUR', // Montenegro → Euro (unilateral adoption)
  'ML': 'XOF', // Mali → West African CFA Franc
  'MT': 'EUR', // Malta → Euro (Eurozone)
  'MW': 'MWK', // Malawi → Malawian Kwacha
  'MX': 'MXN', // Mexico → Mexican Peso
  'MY': 'MYR', // Malaysia → Malaysian Ringgit
  'NG': 'NGN', // Nigeria → Nigerian Naira
  'NL': 'EUR', // Netherlands → Euro (Eurozone)
  'NO': 'NOK', // Norway → Norwegian Krone
  'NP': 'NPR', // Nepal → Nepalese Rupee
  'NZ': 'NZD', // New Zealand → New Zealand Dollar
  'OM': 'OMR', // Oman → Omani Rial
  'PE': 'PEN', // Peru → Peruvian Sol
  'PH': 'PHP', // Philippines → Philippine Peso
  'PL': 'PLN', // Poland → Polish Zloty
  'PS': 'ILS', // Palestine → Israeli New Shekel (primary currency in circulation)
  'PT': 'EUR', // Portugal → Euro (Eurozone)
  'PY': 'PYG', // Paraguay → Paraguayan Guarani
  'RO': 'RON', // Romania → Romanian Leu
  'RS': 'RSD', // Serbia → Serbian Dinar
  'RU': 'RUB', // Russia → Russian Ruble
  'SA': 'SAR', // Saudi Arabia → Saudi Riyal
  'SE': 'SEK', // Sweden → Swedish Krona
  'SG': 'SGD', // Singapore → Singapore Dollar
  'SK': 'EUR', // Slovakia → Euro (Eurozone)
  'SV': 'USD', // El Salvador → US Dollar (dollarized)
  'TH': 'THB', // Thailand → Thai Baht
  'TR': 'TRY', // Turkey → Turkish Lira
  'TW': 'TWD', // Taiwan → New Taiwan Dollar
  'US': 'USD', // United States → US Dollar
  'ZA': 'ZAR', // South Africa → South African Rand
} as const satisfies Readonly<Record<string, string>>;

/**
 * Country code to full English name mapping
 *
 * Maps Finnhub's 86 country codes to human-readable country names.
 * Used for display in UI country selection components.
 *
 * Note: "EU" is labeled "Eurozone" to match economic context (aggregate data).
 *
 * Sorted alphabetically by country code to match COUNTRY_TO_CURRENCY.
 */
export const COUNTRY_NAMES = {
  'AE': 'United Arab Emirates',
  'AL': 'Albania',
  'AO': 'Angola',
  'AR': 'Argentina',
  'AT': 'Austria',
  'AU': 'Australia',
  'AZ': 'Azerbaijan',
  'BD': 'Bangladesh',
  'BE': 'Belgium',
  'BG': 'Bulgaria',
  'BN': 'Brunei',
  'BR': 'Brazil',
  'BW': 'Botswana',
  'BY': 'Belarus',
  'CA': 'Canada',
  'CD': 'DR Congo',
  'CH': 'Switzerland',
  'CN': 'China',
  'CO': 'Colombia',
  'CV': 'Cape Verde',
  'CZ': 'Czechia',
  'DE': 'Germany',
  'DO': 'Dominican Republic',
  'EC': 'Ecuador',
  'EE': 'Estonia',
  'ER': 'Eritrea',
  'ES': 'Spain',
  'ET': 'Ethiopia',
  'EU': 'Eurozone',
  'FI': 'Finland',
  'FR': 'France',
  'GB': 'United Kingdom',
  'GE': 'Georgia',
  'GR': 'Greece',
  'HK': 'Hong Kong',
  'HR': 'Croatia',
  'HU': 'Hungary',
  'ID': 'Indonesia',
  'IE': 'Ireland',
  'IL': 'Israel',
  'IN': 'India',
  'IR': 'Iran',
  'IT': 'Italy',
  'JO': 'Jordan',
  'JP': 'Japan',
  'KG': 'Kyrgyzstan',
  'KM': 'Comoros',
  'KR': 'South Korea',
  'KW': 'Kuwait',
  'KZ': 'Kazakhstan',
  'LK': 'Sri Lanka',
  'LT': 'Lithuania',
  'LU': 'Luxembourg',
  'LV': 'Latvia',
  'MD': 'Moldova',
  'ME': 'Montenegro',
  'ML': 'Mali',
  'MT': 'Malta',
  'MW': 'Malawi',
  'MX': 'Mexico',
  'MY': 'Malaysia',
  'NG': 'Nigeria',
  'NL': 'Netherlands',
  'NO': 'Norway',
  'NP': 'Nepal',
  'NZ': 'New Zealand',
  'OM': 'Oman',
  'PE': 'Peru',
  'PH': 'Philippines',
  'PL': 'Poland',
  'PS': 'Palestine',
  'PT': 'Portugal',
  'PY': 'Paraguay',
  'RO': 'Romania',
  'RS': 'Serbia',
  'RU': 'Russia',
  'SA': 'Saudi Arabia',
  'SE': 'Sweden',
  'SG': 'Singapore',
  'SK': 'Slovakia',
  'SV': 'El Salvador',
  'TH': 'Thailand',
  'TR': 'Turkey',
  'TW': 'Taiwan',
  'US': 'United States',
  'ZA': 'South Africa',
} as const satisfies Readonly<Record<string, string>>;

// Dev-only runtime validation guards
// These checks catch data integrity issues during development but are stripped from production builds
if (import.meta.env.MODE !== 'production') {
  const entries = Object.entries(COUNTRY_TO_CURRENCY);

  // Validation #1: Check all country codes are 2-letter uppercase (or "EU")
  const countryCodeRegex = /^[A-Z]{2}$/;
  for (const [countryCode] of entries) {
    if (countryCode !== 'EU' && !countryCodeRegex.test(countryCode)) {
      throw new Error(
        `[Economic Calendar Constants] Invalid country code format: "${countryCode}". ` +
        `Expected 2-letter uppercase (A-Z) or "EU".`
      );
    }
  }

  // Validation #2: Check all currency codes are 3-letter uppercase
  const currencyCodeRegex = /^[A-Z]{3}$/;
  for (const [countryCode, currencyCode] of entries) {
    if (!currencyCodeRegex.test(currencyCode)) {
      throw new Error(
        `[Economic Calendar Constants] Invalid currency code format for ${countryCode}: "${currencyCode}". ` +
        `Expected 3-letter uppercase ISO 4217 code.`
      );
    }
  }

  // Validation #3: Check expected count matches Finnhub API (86 countries)
  const expectedCount = 86;
  if (entries.length !== expectedCount) {
    throw new Error(
      `[Economic Calendar Constants] COUNTRY_TO_CURRENCY has ${entries.length} entries, ` +
      `expected ${expectedCount} to match Finnhub API country list.`
    );
  }

  // Validation #4: Check COUNTRY_NAMES keys match COUNTRY_TO_CURRENCY keys exactly
  const currencyKeys = Object.keys(COUNTRY_TO_CURRENCY).sort();
  const nameKeys = Object.keys(COUNTRY_NAMES).sort();

  if (currencyKeys.length !== nameKeys.length) {
    throw new Error(
      `[Economic Calendar Constants] COUNTRY_NAMES has ${nameKeys.length} entries, ` +
      `but COUNTRY_TO_CURRENCY has ${currencyKeys.length}. Keys must match exactly.`
    );
  }

  for (let i = 0; i < currencyKeys.length; i++) {
    if (currencyKeys[i] !== nameKeys[i]) {
      throw new Error(
        `[Economic Calendar Constants] Key mismatch between COUNTRY_TO_CURRENCY and COUNTRY_NAMES. ` +
        `Expected "${currencyKeys[i]}" but found "${nameKeys[i]}" at position ${i}.`
      );
    }
  }
}
